/**
 * 框架通道（PAYERMAX）配置的读查询（A8 §10.0 / §10.1）。// @real
 *
 * 消费方：「系统设置 / 集成」下的通道配置页与入口卡；支付页渠道卡、运营弹窗落点、App 支付通道策略的
 * 「收单前置」提示（`useAcquiringPrerequisiteStates` / `usePaymentProviderConfigs`，判据见
 * `~/data/adapters/acquiring-prerequisite`）。各处共用 `paymentProviderQueryKeys.configs` 同一份缓存，
 * 配置页写操作成功后失效配置列表即同步刷新这些提示（能力声明不随写入变化）。
 */

import { PAYMENT_PROVIDER_CHANNELS, type PaymentProviderChannel } from '@novel-admin/shared';
import { useQueries, useQuery } from '@tanstack/react-query';
import { paymentProviderConfigApi } from '~/hooks/use-payment-provider-config';
import {
  acquiringPrerequisiteOf,
  describeAcquiringPrerequisite,
} from './adapters/acquiring-prerequisite';

export const paymentProviderQueryKeys = {
  capability: (channel: PaymentProviderChannel) => [
    'integrations',
    'payment-providers',
    channel,
    'capability',
  ],
  configs: (channel: PaymentProviderChannel) => [
    'integrations',
    'payment-providers',
    channel,
    'configs',
  ],
};

/** 能力声明：凭据字段闭集、适用平台与当前部署环境 mode。读取失败时配置页禁用新增 / 编辑。 */
export function usePaymentProviderCapability(channel: PaymentProviderChannel | null) {
  return useQuery({
    queryKey: paymentProviderQueryKeys.capability(channel ?? 'PAYERMAX'),
    enabled: channel !== null,
    queryFn: () => paymentProviderConfigApi.capability(channel as PaymentProviderChannel),
  });
}

/**
 * 配置列表（含每个配置所属商户账户组的 endpoint 摘要与最近一次测试结果）。
 * 读取失败必须暴露为 error：页面与入口卡据此 fail-closed，不能把失败当成「未配置」。
 */
export function usePaymentProviderConfigs(channel: PaymentProviderChannel | null) {
  return useQuery({
    queryKey: paymentProviderQueryKeys.configs(channel ?? 'PAYERMAX'),
    enabled: channel !== null,
    queryFn: () => paymentProviderConfigApi.listConfigs(channel as PaymentProviderChannel),
  });
}

/** 框架通道配置页路由（系统设置 › 集成 › 该通道）；收单前置提示统一链到这里 */
export function paymentProviderConfigRoute(channel: PaymentProviderChannel): string {
  return `/settings/integrations/payment-providers/${channel}`;
}

/** 某结算通道的收单前置现状：`problem` = 未就绪描述（null = 无已知问题），`unknown` = 读不到配置、无法判定 */
export type AcquiringPrerequisiteState = { problem: string | null; unknown: boolean };

const NO_ACQUIRING_PROBLEM: AcquiringPrerequisiteState = { problem: null, unknown: false };

/**
 * 本 App 各框架通道的收单前置（字典 65 §1），返回按结算通道查询的函数：非框架通道恒「无已知问题」；
 * 首载中不下结论；读取失败或未取到（含离线挂起）记为 `unknown`，调用方按「未知 ≠ 已关闭」处理。
 * 与 `usePaymentProviderConfigs` 同 query key、同 queryFn，共用一份缓存。
 */
export function useAcquiringPrerequisiteStates(
  appId: string,
  opts: { isNativeApp: boolean; enabled?: boolean },
): (channel: string) => AcquiringPrerequisiteState {
  const enabled = opts.enabled ?? true;
  const lists = useQueries({
    queries: PAYMENT_PROVIDER_CHANNELS.map((channel) => ({
      queryKey: paymentProviderQueryKeys.configs(channel),
      enabled,
      queryFn: () => paymentProviderConfigApi.listConfigs(channel),
    })),
  });
  return (channel) => {
    const index = PAYMENT_PROVIDER_CHANNELS.findIndex((candidate) => candidate === channel);
    const state = index === -1 ? undefined : lists[index];
    if (!state || state.isLoading) return NO_ACQUIRING_PROBLEM;
    if (state.data === undefined) return { problem: null, unknown: true };
    return {
      problem: describeAcquiringPrerequisite(
        acquiringPrerequisiteOf(state.data, appId, { isNativeApp: opts.isNativeApp }),
      ),
      unknown: false,
    };
  };
}
